import Cookie from 'cookie';

import { parseFullName } from '@jerry-serverless/name-parser';

import type { AppData } from '../../types/app_data';
import type { Address } from '../../types/address';

import type { AnalyticsUser } from './types';

const DATA_PROCESSING_OPTIONS_STATE_MAP: Record<string, number> = {
  CA: 1000,
  CO: 1001,
  CT: 1002,
};

/**
 * Check if LDU should be enabled for Facebook.
 */
const checkEnableLDUForFacebook = (options: { enableLDU?: boolean; address?: Address | null }): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!window?.fbq) {
    return;
  }
  /**
   * Enable LDU for CA, CO, CT.
   * @see https://developers.facebook.com/docs/marketing-apis/data-processing-options
   */
  if (options.enableLDU) {
    window.fbq(
      'dataProcessingOptions',
      ['LDU'],
      1,
      DATA_PROCESSING_OPTIONS_STATE_MAP[options.address?.state || ''] || 0
    );
  }
};

/**
 * Identify user to Facebook Pixel.
 *
 * @param user
 */
export const setUserForFacebookPixel = async (user: AnalyticsUser): Promise<void> => {
  if (window.fbq && window.fbPixelID) {
    const cookie = Cookie.parse(document.cookie);

    const { first: fn, last: ln } = parseFullName(user.name, 'all', -1, 0, 1);
    const userObj: Record<string, string> = {
      external_id: user.id,
      fn,
      ln,
      ph: `1${user.phone}`,
      fbc: cookie?._fbc,
      fbp: cookie?._fbp,
    };

    checkEnableLDUForFacebook(user);
    window.fbq('init', window.fbPixelID, userObj);
  }
};

/**
 * Track signup conversion.
 */
export const trackSignedUpForFacebook = (appData?: AppData): void => {
  const { signupParams } = appData || {};

  checkEnableLDUForFacebook({ enableLDU: appData?.enableLDU, address: appData?.address });

  window.fbq?.('track', 'CompleteRegistration');
  if (signupParams?.dwepService) {
    window.fbq?.('trackCustom', 'SignedUpForDwep');
  }
};

/**
 * Track onboarded conversion.
 */
export const trackOnboardedForFacebook = (appData?: AppData): void => {
  const { signupParams } = appData || {};

  checkEnableLDUForFacebook({ enableLDU: appData?.enableLDU, address: appData?.address });

  window.fbq?.('track', 'Subscribe');
  if (signupParams?.dwepService) {
    window.fbq?.('trackCustom', 'OnboardedForDwep');
  }
};
